import Phaser from 'phaser';
import { SceneHelper} from '../utils/SceneHelper';
import { Solar } from './Solar';
import { GameConfig } from '../GameConfig';
import { Button } from '../controls/buttons/Button';

export class WelcomeScene extends Phaser.Scene {
    private imgBg!: Phaser.GameObjects.Image;
    private lblTitle!: Phaser.GameObjects.Text;
    private btnFullScreen!: Phaser.GameObjects.Text;
    private lblCopyright!: Phaser.GameObjects.Text;
    private btnEarthMoon!: Button;
    private btnSunEarth!: Button;
    private btnPlanet2!: Button;
    private btnPlanet3!: Button;
    private btnSolarSystem!: Button;

    //--------------------------------------------------
    // Life Cycle
    //--------------------------------------------------
    constructor() {
        super('WelcomeScene');
    }

    preload() {
        SceneHelper.showLoading(this);

        // bgm and bg
        this.load.image('bg',  'assets/galaxy.jpg');
        this.load.audio('bgm', 'assets/bg.mp3');
        this.load.image(Solar.sun.key,     Solar.sun.path);
        this.load.image(Solar.moon.key,    Solar.moon.path);
        this.load.image(Solar.mercury.key, Solar.mercury.path);
        this.load.image(Solar.venus.key,   Solar.venus.path);
        this.load.image(Solar.earth.key,   Solar.earth.path);
        this.load.image(Solar.mars.key,    Solar.mars.path);
        this.load.image(Solar.jupiter.key, Solar.jupiter.path);
        this.load.image(Solar.saturn.key,  Solar.saturn.path);
        this.load.image(Solar.uranus.key,  Solar.uranus.path);
        this.load.image(Solar.neptune.key, Solar.neptune.path);
        this.load.image(Solar.pluto.key,   Solar.pluto.path);
    }

    create() {
        this.sound.add('bgm').play({ loop: true, volume: 0.5 });
        var width = this.cameras.main.width;
        var height = this.cameras.main.height;
        this.createUI(width, height);
        this.createEntries(width, height);
        this.scale.on('resize', this.resize, this);
        this.resize(new Phaser.Structs.Size(this.game.scale.width, this.game.scale.height));
    }

    /**自适应屏幕大小变更 */
    resize(gameSize: Phaser.Structs.Size) {
        const width = gameSize.width;
        const height = gameSize.height;
        this.imgBg.setPosition(width / 2, height / 2).setDisplaySize(width*2, height*2);
        this.btnFullScreen.setPosition(width - 120, 20);
        this.lblTitle.setPosition(width/2, height*0.2);
        this.lblCopyright.setPosition(width/2, height - 30);

        var startY = height*0.6;
        this.btnEarthMoon.setPosition(width / 2, startY).setOrigin(0.5, 0);
        this.btnSunEarth.setPosition(width / 2, startY + 50).setOrigin(0.5, 0);
        this.btnPlanet2.setPosition(width / 2, startY + 100).setOrigin(0.5, 0);
        this.btnPlanet3.setPosition(width / 2, startY + 150).setOrigin(0.5, 0);
        this.btnSolarSystem.setPosition(width / 2, startY + 200).setOrigin(0.5, 0);
    }

    //--------------------------------------------------
    // UI
    //--------------------------------------------------
    /**创建UI */
    private createUI(width: number, height: number) {
        var centerX = width / 2;
        var centerY = height / 2;

        // 背景
        this.imgBg = this.add.image(centerX, centerY, 'bg');
        this.imgBg.displayWidth = this.scale.width * 2;
        this.imgBg.displayHeight = this.scale.height * 2;
        this.tweens.add({
            targets: this.imgBg,
            scaleX: 1.2,
            scaleY: 1.2,
            duration: 10000,
            ease: 'Sine.easeInOut',
            yoyo: true,
            repeat: -1
        });

        // 全屏按钮
        this.btnFullScreen = this.add.text(width - 150, 20, 'Full Screen', { font: '18px Arial', color: '#ffffff' })
            .setOrigin(0, 0)
            .setInteractive()
            .on('pointerdown', () => {
                this.game.scale.toggleFullscreen();
            })
            .setVisible(this.game.scale.fullscreen.available);

        // 标题
        this.lblTitle = this.add.text(centerX, height * 0.2, 'Planets', { font: '48px Arial', color: '#ffff00' }).setOrigin(0.5);

        // 创建版权信息
        this.lblCopyright = this.add.text(
            width / 2,
            height - 30,
            'surfsky@189.cn\rCopyright 2025',
            {
                fontSize: '14px',
                color: '#ffffff',
            }
        ).setOrigin(0.5).setInteractive().on('pointerdown', () => {
            this.lblCopyright.setScale(0.9);
        }).on('pointerup', () => {
            this.lblCopyright.setScale(1.0);
            this.sound.stopByKey('bgm');
            SceneHelper.goScene(this, 'AboutScene');
        });
    }

    /**创建游戏入口 */
    private createEntries(width:number, height: number) {
        const size = 200;
        var centerX = width /2; - 100;
        var centerY = height / 2;
        var startY = height / 2 - 200;

        /*
        地球和月球。
        以地球直径为单位 1d，地球质量为单位 1w，地球公转速度为 1s 时.
        月球
            直径：约 0.273d
            与地球的距离：约 30d（平均距离约 384400 千米，地球直径约 12742 千米）
            质量：约 0.0123w
            公转速度：约 0.055s（月球绕地球公转速度约 1.022 km/s，地球公转速度约 29.78 km/s ）
        */
        this.btnEarthMoon = new Button(this, centerX, startY, 'Earth & Moon', { mode: 'text', textColor: 0xffffff})
            .onClick(() => {
                this.scene.start('PlanetScene', {
                    name: 'Earth & Moon',
                    zoom: 1,
                    showOwn: false,
                    showForce: false,
                    showTrail: true,
                    planets: [
                        { name: 'Earth', image: Solar.earth.key,  x: centerX,       y: centerY, mass: 1,      size: 100, color: 0x4fc3f7, velocity: { x: 0, y: 0 }, fix: true },
                        { name: 'Moon',  image: Solar.moon.key,   x: centerX + 300, y: centerY, mass: 0.0123, size: 27,  color: 0xffb74d, velocity: { x: 0, y: 60 } },
                    ]
                });
            });

        // 太阳和地球和月球
        this.btnSunEarth = new Button(this, centerX, startY + 50, 'Sun & Earth & Moon', { mode: 'text', textColor: 0xffffff})
           .onClick(() => {
                this.scene.start('PlanetScene', {
                    name: 'Sun & Earth & Moon',
                    zoom: 10,
                    showOwn: false,
                    showForce: false,
                    showTrail: true,
                    planets: [
                        { name: 'Sun', image: Solar.sun.key, x: centerX, y: centerY, mass: 333000 * 0.00001, size: 109, color: 0xffff00, velocity: { x: 0, y: 0 }, fix: true },
                        { name: 'Earth', image: Solar.earth.key, x: centerX + 600, y: centerY, mass: 3, size: 10, color: 0x4fc3f7, velocity: { x: 0, y: 70 } },
                        { name: 'Moon', image: Solar.moon.key, x: centerX + 650, y: centerY, mass: 0.0123, size: 2.7, color: 0xffb74d, velocity: { x: 0, y: 310 } },
                    ]
            });
        });

        // 质量相近的两个行星
        this.btnPlanet2 = new Button(this, centerX, startY + 100, '2 Planets', { mode: 'text', textColor: 0xffffff})
            .onClick(() => {
                this.scene.start('PlanetScene', {
                    name: 'Planets 2',
                    planets: [
                        { name: 'Planet1', image: Solar.earth.key, x: centerX - 300, y: centerY, mass: 1.2, size: 120, color: 0x4fc3f7, velocity: { x: 0, y: 30 } },
                        { name: 'Planet2', image: Solar.mars.key, x: centerX + 300, y: centerY, mass: 1.0, size: 110, color: 0xffb74d, velocity: { x: 0, y: -30 } },
                    ]
            });
        });

        // 三个质量相近的行星
        this.btnPlanet3 = new Button(this, centerX, startY + 150, '3 Planets', { mode: 'text', textColor: 0xffffff})
            .onClick(() => {
                this.scene.start('PlanetScene', {
                    name: 'Three bodies',
                    zoom: 1,
                    planets: [
                        { name: 'Planet1', image: Solar.earth.key, x: centerX - 400, y: 200, mass: 1.1, size: 55, color: 0x4fc3f7, tingColor: 0x4fc3f7, velocity: { x: 40, y: 0 } },
                        { name: 'Planet2', image: Solar.venus.key, x: centerX + 400, y: 200, mass: 1.0, size: 50, color: 0xffb74d, tingColor: 0xffb74d, velocity: { x: 0, y: 40 } },
                        { name: 'Planet3', image: Solar.pluto.key, x: centerX, y: 800, mass: 0.9, size: 45, color: 0x81c784, tingColor: 0x81c784, velocity: { x: -50, y: 0 } },
                    ]
            });
        });


        /*
        以地球直径为单位 1d，地球质量为单位 1w，地球公转速度为 1s 时，太阳系相关天体的数据对比
        AU：地球到太阳平均距离约 1.496 亿千米，1AU 约 11742 个地球直径
        太阳
            直径：约 109d
            质量：约 333000w
        行星	直径	    与太阳的距离                                         质量         公转速度
        -------------------------------------------------------------------------------------------
        水星	约 0.383d	约 0.39AU（相当于约 57.9 百万千米，约 4580 个地球直径）	 约 0.0553w   约 1.607s
        金星	约 0.949d	约 0.72AU（约 108.2 百万千米，约 8370 个地球直径）      约 0.815w    约 1.174s
        地球	1d	       1AU（约 149.6 百万千米，约 11742 个地球直径）	       1w           1s
        火星	约 0.532d	约 1.52AU（约 227.9 百万千米，约 17888 个地球直径）     约 0.107w    约 0.802s
        木星	约 11.209d	约 5.20AU（约 778.5 百万千米，约 60930 个地球直径）     约 317.8w    约 0.439s
        土星	约 9.449d	约 9.58AU（约 1433.5 百万千米，约 112517 个地球直径）   约 95.2w     约 0.325s
        天王星	约 4.007d	约 19.22AU（约 2872.5 百万千米，约 225504 个地球直径）  约 14.5w     约 0.228s
        海王星	约 3.883d	约 30.11AU（约 4495.1 百万千米，约 353000 个地球直径）  约 17.1w     约 0.182s
        */
        this.btnSolarSystem = new Button(this, centerX, startY + 200, 'Solar System', { mode: 'text', textColor: 0xffffff})
            .onClick(() => {
                this.scene.start('PlanetScene', {
                    name: 'Solar System',
                    zoom: 1,
                    planets: [
                        { name: 'Sun', image: Solar.sun.key, x: centerX, y: centerY, mass: 333000 * 0.0001, size: 109, color: 0xffff00, velocity: { x: 0, y: 0 }, fix: true },
                        { name: 'Mercury', image: Solar.mercury.key, x: centerX + 400, y: centerY, mass: 0.0553, size: 0.383 * 10, color: 0x808080, velocity: { x: 0, y: 4.74 * 70 } },
                        { name: 'Venus', image: Solar.venus.key, x: centerX + 600, y: centerY, mass: 0.815, size: 0.949 * 10, color: 0xffa500, velocity: { x: 0, y: 3.502 * 65 } },
                        { name: 'Earth', image: Solar.earth.key, x: centerX + 850, y: centerY, mass: 1, size: 1 * 10, color: 0x0000ff, velocity: { x: 0, y: 2.978 * 55 } },
                    ]
            });
        });
    }


}